import { useState } from "react";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import UserSingleIcon from "../../../assets/svg/user-single-icon";
import {
  selectFilterState,
  setCommunicationsVisitorPresence,
} from "../../../redux/slices/filterSlice";
import { useDispatch, useSelector } from "react-redux";

const VISITOR_PRESENCE_OPTIONS = [
  {
    value: "",
    displayValue: "Any status",
    description: "All VerbaChat conversations, online or not",
    iconColor: "text-verba-blue dark:text-verba-blue-dk",
    dotColor: "bg-verba-blue dark:bg-verba-blue-dk",
  },
  {
    value: "online",
    displayValue: "Online now",
    description: "Visitor is still active on the chat widget",
    iconColor: "text-verba-green-success dark:text-verba-green-success-dk",
    dotColor: "bg-verba-green-success dark:bg-verba-green-success-dk",
  },
  {
    value: "offline",
    displayValue: "Left the chat",
    description: "Visitor closed the tab or went inactive / offline",
    iconColor: "text-analytics-gray dark:text-analytics-gray-dk",
    dotColor: "bg-gray-placeholder dark:bg-gray-placeholder-dk",
  },
];

const VisitorPresenceFilter = ({ onClose }: { onClose: () => void }) => {
  const dispatch = useDispatch();
  const filterState = useSelector(selectFilterState);
  const currentPresence = filterState?.communications?.visitorPresence;

  const [selectedValue, setSelectedValue] = useState<string>(
    (currentPresence?.value as string) ?? ""
  );

  const handleApply = () => {
    if (selectedValue === (currentPresence?.value ?? "")) return;
    const option = VISITOR_PRESENCE_OPTIONS.find(
      (o) => o.value === selectedValue
    );
    dispatch(
      setCommunicationsVisitorPresence({
        value: selectedValue,
        displayValue: option?.displayValue ?? "",
      })
    );
    onClose();
  };

  const isApplyDisabled = selectedValue === (currentPresence?.value ?? "");

  return (
    <div className="flex flex-col gap-1.5 mt-2 px-3">
      <h2 className="text-md text-heading-text dark:text-heading-text-dk py-1.5">
        Show VerbaChat conversations by whether the visitor is still on the
        widget right now.
      </h2>
      {VISITOR_PRESENCE_OPTIONS.map((option) => (
        <div
          key={option.value}
          onClick={() => setSelectedValue(option.value)}
          className={`flex items-center justify-between border cursor-pointer rounded-lg py-3 px-3 ${
            selectedValue === option.value
              ? "border-verba-blue dark:border-verba-blue-dk bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk"
              : "border-border-gray dark:border-border-gray-dk"
          }`}
        >
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <UserSingleIcon
                className={`w-[18px] h-[18px] ${option.iconColor}`}
              />
              <span
                className={`absolute -bottom-0.5 -right-0.5 w-[7px] h-[7px] rounded-full border border-pure-white dark:border-pure-white-dk ${option.dotColor}`}
              />
            </div>
            <div className="flex flex-col">
              <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
                {option.displayValue}
              </span>
              <span className="text-xs text-dark-gray dark:text-dark-gray-dk">
                {option.description}
              </span>
            </div>
          </div>
          {selectedValue === option.value && (
            <CheckIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
          )}
        </div>
      ))}
      <div className="flex justify-end gap-2 mt-4">
        <Button
          innerText="Cancel"
          onClick={onClose}
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
        />
        <Button
          onClick={handleApply}
          disabled={isApplyDisabled}
          innerText="Apply"
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
        />
      </div>
    </div>
  );
};

export default VisitorPresenceFilter;
